import { sortLabelSx } from "@app/theme/tokens";
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { useState } from "react";
import type { SpofRow } from "../types";
import { mw } from "../utils/format";
import { spofCardHref } from "../utils/links";
import { RiskChip } from "./Chips";
import DashboardCard from "./DashboardCard";
import { PendingCardBody, PendingValue } from "./PendingValue";

type SortKey = keyof Pick<
  SpofRow,
  "spof_id" | "power_loss_mw" | "risk_score" | "residual_risk_score"
>;

/** Risk columns rank on the score of the register, not on the label they display. */
const COLUMNS: { key: SortKey | null; label: string; align?: "right" }[] = [
  { key: "spof_id", label: "SPOF ID" },
  { key: null, label: "Description" },
  { key: "power_loss_mw", label: "Power loss (MW)", align: "right" },
  { key: "risk_score", label: "Current risk" },
  { key: "residual_risk_score", label: "Residual risk" },
];

function compare(a: SpofRow, b: SpofRow, key: SortKey) {
  const left = a[key];
  const right = b[key];
  if (typeof left === "string" || typeof right === "string") {
    return String(left).localeCompare(String(right));
  }
  return (left ?? -1) - (right ?? -1);
}

export default function SpofTable({
  plantId,
  spofs,
}: {
  plantId: string;
  spofs: SpofRow[];
}) {
  const [sortKey, setSortKey] = useState<SortKey>("spof_id");
  const [descending, setDescending] = useState(false);

  const sorted = [...spofs].sort(
    (a, b) => compare(a, b, sortKey) * (descending ? -1 : 1),
  );

  const sortBy = (key: SortKey) => {
    setDescending(key === sortKey ? !descending : key !== "spof_id");
    setSortKey(key);
  };

  return (
    <DashboardCard title="Priority SPOFs">
      {spofs.length === 0 ? (
        <PendingCardBody hint="No SPOF matches the current selection." />
      ) : (
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map(({ key, label, align }) => (
                  <TableCell key={label} align={align} sx={{ fontWeight: 600 }}>
                    {key === null ? (
                      label
                    ) : (
                      <TableSortLabel
                        active={sortKey === key}
                        direction={
                          sortKey === key && descending ? "desc" : "asc"
                        }
                        onClick={() => sortBy(key)}
                        sx={sortLabelSx(sortKey === key)}
                      >
                        {label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((spof) => (
                <TableRow key={spof.spof_id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Link href={spofCardHref(plantId, spof.spof_id)}>
                      {spof.spof_id}
                    </Link>
                  </TableCell>
                  <TableCell>{spof.description ?? <PendingValue />}</TableCell>
                  <TableCell align="right">
                    {mw(spof.power_loss_mw) ?? <PendingValue />}
                  </TableCell>
                  <TableCell>
                    <RiskChip level={spof.risk_category} />
                  </TableCell>
                  <TableCell>
                    <RiskChip level={spof.residual_risk_category} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DashboardCard>
  );
}
